import { Heart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function LandingPage() {
  const navigate = useNavigate();

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="hero-gradient">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fade-in">
              <div className="inline-flex items-center gap-2">
                <Heart className="w-8 h-8 text-accent" fill="currentColor" />
                <span className="font-heading text-2xl text-primary">Dockter</span>
              </div>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-text-primary">
                Your Doctor,
                <br />
                <span className="text-primary">Always Connected</span>
              </h1>
              <p className="text-base md:text-lg leading-relaxed text-text-secondary font-body max-w-xl">
                Connect with your doctor seamlessly. Manage appointments, prescriptions, and your health regimen all in one warm, approachable platform.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  data-testid="get-started-btn"
                  onClick={handleLogin}
                  className="btn-primary"
                >
                  Get Started
                </button>
                <button
                  onClick={() => navigate('/about')}
                  className="btn-outline"
                >
                  Learn More
                </button>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1764727291644-5dcb0b1a0375?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBsdXh1cnklMjBtZWRpY2FsJTIwY2xpbmljJTIwcmVjZXB0aW9uJTIwd2FybSUyMGludGVyaW9yfGVufDB8fHx8MTc3MjU1OTEzMnww&ixlib=rb-4.1.0&q=85"
                alt="Modern clinic reception"
                className="rounded-xl shadow-floating w-full h-auto"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="text-center mb-16">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-text-primary mb-4">
            Everything You Need
          </h2>
          <p className="text-text-secondary font-body text-base md:text-lg">Comprehensive healthcare management at your fingertips</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div className="card-default" data-testid="feature-appointments">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="font-heading text-xl font-semibold text-text-primary mb-2">Scheduling</h3>
            <p className="text-text-secondary font-body">Request appointments with your doctor with our simple approval system.</p>
          </div>

          <div className="card-default" data-testid="feature-refills">
            <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h3 className="font-heading text-xl font-semibold text-text-primary mb-2">Prescription Refills</h3>
            <p className="text-text-secondary font-body">Quickly request medication refills and track their status in real-time.</p>
          </div>

          <div className="card-default" data-testid="feature-regimen">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="font-heading text-xl font-semibold text-text-primary mb-2">Health Regimen</h3>
            <p className="text-text-secondary font-body">Access your personalized treatment plan and medication schedule.</p>
          </div>

          <div className="card-default" data-testid="feature-chat">
            <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
            </div>
            <h3 className="font-heading text-xl font-semibold text-text-primary mb-2">AI Health Assistant</h3>
            <p className="text-text-secondary font-body">Get instant answers to health questions from our AI-powered assistant.</p>
          </div>

          <div className="card-default" data-testid="feature-info">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h3 className="font-heading text-xl font-semibold text-text-primary mb-2">Patient Profile</h3>
            <p className="text-text-secondary font-body">Keep all your medical information and history in one secure place.</p>
          </div>

          <div className="card-default" data-testid="feature-secure">
            <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h3 className="font-heading text-xl font-semibold text-text-primary mb-2">Secure & Private</h3>
            <p className="text-text-secondary font-body">Your health data is encrypted and protected with industry-leading security.</p>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="accent-gradient py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-6">
            Ready to Transform Your Healthcare Experience?
          </h2>
          <p className="text-primary-foreground/90 font-body text-base md:text-lg mb-8">
            Join Dockter today and experience seamless healthcare management.
          </p>
          <button
            data-testid="cta-get-started-btn"
            onClick={handleLogin}
            className="bg-white text-primary rounded-full px-8 py-3 hover:bg-background-subtle transition-colors font-medium shadow-lg"
          >
            Get Started Now
          </button>
        </div>
      </div>
    </div>
  );
}